import type { Temporal } from 'temporal-polyfill';
import type { MaybeRefOrGetter } from 'vue';
import { computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import {
	formatTimezoneLabel,
	generateTimezoneTooltip,
	TIMEZONE_LOCAL,
} from '#layers/base/utils/timezone';

export interface UseTimezoneOptions {
	timezone?: MaybeRefOrGetter<string>;
	dateTime?: MaybeRefOrGetter<Temporal.PlainDateTime | null>;
	locale?: MaybeRefOrGetter<string>;
}

export function useTimezone(options: UseTimezoneOptions = {}) {
	const {
		timezone: timezoneOption = TIMEZONE_LOCAL,
		dateTime: dateTimeOption = null,
		locale: localeOption = 'en-US',
	} = options;

	const { t } = useI18n();

	const timezone = toRef(timezoneOption);
	const dateTime = toRef(dateTimeOption);
	const locale = toRef(localeOption);

	const timezoneLabel = computed(() =>
		formatTimezoneLabel(
			timezone.value,
			dateTime.value ?? undefined,
			'short',
			locale.value,
		),
	);
	const tooltipContent = computed(() => {
		if (!dateTime.value) return null;

		const tooltip = generateTimezoneTooltip(
			dateTime.value,
			timezone.value,
			locale.value,
		);

		if (!tooltip) {
			return null;
		}

		return t('blocks.input_datetime.utc', { datetime: tooltip });
	});
	return {
		timezoneLabel,
		tooltipContent,
	};
}
